import { Component, OnDestroy, OnInit } from '@angular/core';
import { SearchService } from '../../../../services/search/search.service';
import { LOGIN_STATE, SharedService } from '../../../../services/shared/shared.service';
import { StorageService } from '../../../../services/storage/storage.service';
import { Params, ActivatedRoute } from '@angular/router';
import { RoutingService } from '../../../../services/routing/routing.service';
import { Definitions } from '../../../../core/definitions';
import { SearchRequestPayload } from '../../../../core/interfaces/search.interface';
import { map, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { OpacSettingsService } from '../../../../services/opac-settings/opac-settings.service';

@Component({
  selector: 'app-open-search',
  templateUrl: './open-search.component.html',
  styleUrls: ['./open-search.component.scss'],
})
export class OpenSearchComponent implements OnInit, OnDestroy {
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  cid: string;
  lid: string;

  opt1: string;
  index: string;
  searchTerm: string;

  opt2: string;
  index2: string;
  searchTerm2: string;

  opt3: string;
  index3: string;
  searchTerm3: string;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _opacSettingsService: OpacSettingsService,
    private _searchService: SearchService,
    private _sharedService: SharedService,
    private _storageService: StorageService,
    private _routingService: RoutingService,
  ) {}

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
  }

  ngOnInit() {
    this._activatedRoute.queryParams.subscribe((params: Params) => {
      this.cid = params['cid'];
      this.lid = params['lid'];

      this.opt1 = params['opt1'];
      this.index = params['index'] ?? 'all_headings';
      this.searchTerm = params['term'];

      this.opt2 = params['opt2'];
      this.index2 = params['index2'] ?? 'all_headings';
      this.searchTerm2 = params['term2'];

      this.opt3 = params['opt3'];
      this.index3 = params['index3'] ?? 'all_headings';
      this.searchTerm3 = params['term3'];

      if (this.searchTerm.indexOf('+') > -1) {
        const term = this.searchTerm.split('+');
        this.searchTerm = term.join(' ');
      }

      this._sharedService
        .getLoginStatusBS()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((state) => {
          if (state != LOGIN_STATE.COMPLETE) {
            return;
          }

          this._performSearch();
        });
    });
  }

  private _loginRequiredBeforeSearch(): Observable<boolean> {
    return this._opacSettingsService.getLoginOptions().pipe(
      map((response) => {
        if (response) {
          return response.requireLoginBeforSearch;
        } else {
          return false;
        }
      }),
    );
  }

  private _buildSearchRequest(resources): SearchRequestPayload {
    const userData = this._storageService.getUserData();
    const opacOptions = userData.opacOptions;
    const simpleSearchOptions = opacOptions.simpleSearchOptions;
    const matches = [];

    if (SharedService.isNotEmpty(this.searchTerm)) {
      matches.push({
        index: this.index,
        query: this.searchTerm,
        queryOption: Definitions.QUERY_OPTION,
        matchJoinOperator: this.opt1 ? this.opt1.toUpperCase() : Definitions.MATCH_JOIN_OPERATOR,
      });
    }

    if (SharedService.isNotEmpty(this.searchTerm2)) {
      matches.push({
        index: this.index2,
        query: this.searchTerm2,
        queryOption: Definitions.QUERY_OPTION,
        matchJoinOperator: this.opt2 ? this.opt2.toUpperCase() : Definitions.MATCH_JOIN_OPERATOR,
      });
    }

    if (SharedService.isNotEmpty(this.searchTerm3)) {
      matches.push({
        index: this.index3,
        query: this.searchTerm3,
        queryOption: Definitions.QUERY_OPTION,
        matchJoinOperator: this.opt3 ? this.opt3.toUpperCase() : Definitions.MATCH_JOIN_OPERATOR,
      });
    }

    return {
      searchQuery: {
        matches: matches,
        filters: {
          scopingLevel: simpleSearchOptions.defaultScopingLevel,
        },
        resources: resources,
        startRecord: Definitions.START_RECORD,
        numOfRecords: this._searchService.numOfRecords,
        facetMatches: [],
        sortBy: [userData.opacOptions.searchResultOptions.sortBy],
        orderBy: userData.opacOptions.searchResultOptions.orderBy,
        resultType: userData.opacOptions.searchResultOptions.groupOption,
      },
    };
  }

  private _performSearch() {
    this._loginRequiredBeforeSearch().subscribe((needsLogin) => {
      if (needsLogin) {
        const message = 'You must login to the system before searching the catalog';
        this._sharedService.openSnackBar(message, 'ok');
        this._routingService.goToLogin();
        return;
      }

      const resources = SearchService.getSelectResources(this._searchService.getSearchResources());
      this._searchService.doSearch(this._buildSearchRequest(resources));
    });
  }
}
